// Razorpay Payment Service for Admin Dashboard
import { 
  getCurrentRazorpayConfig, 
  validateRazorpayKeys, 
  generateReceiptId,
  RAZORPAY_CONFIG 
} from '../config/razorpayConfig';

import { 
  RazorpayOptions, 
  RazorpayResponse, 
  RazorpayError,
  PaymentCreationData,
  PaymentRecord
} from '../types/razorpay';

import { collection, addDoc, updateDoc, doc, Timestamp } from 'firebase/firestore';
import { db, functions } from '../services/firebase';
import { httpsCallable } from 'firebase/functions';

class RazorpayService {
  private config: any;
  private razorpayLoadTimeout = 10000; // 10 seconds timeout

  constructor() {
    this.config = getCurrentRazorpayConfig();
    this.validateConfiguration();
    
    // Debug logging for environment
    console.log('🔧 RazorpayService initialized with config:', {
      keyId: this.config.keyId,
      environment: this.config.environment || 'production',
      currency: this.config.currency,
      hospitalName: this.config.hospitalInfo.name
    });
  }

  // Wait for Razorpay SDK to load
  private async waitForRazorpay(): Promise<boolean> {
    return new Promise((resolve) => {
      if (typeof window.Razorpay !== 'undefined') {
        console.log('✅ Razorpay already loaded');
        resolve(true);
        return;
      }

      console.log('⏳ Waiting for Razorpay SDK to load...');
      const startTime = Date.now();
      
      const checkInterval = setInterval(() => {
        if (typeof window.Razorpay !== 'undefined') {
          console.log('✅ Razorpay loaded successfully');
          clearInterval(checkInterval);
          resolve(true);
        } else if (Date.now() - startTime > this.razorpayLoadTimeout) {
          console.error('❌ Razorpay loading timeout');
          clearInterval(checkInterval);
          resolve(false);
        }
      }, 100);
    });
  }

  // Validate Razorpay configuration
  validateConfiguration(): boolean {
    const validation = validateRazorpayKeys(this.config.keyId, this.config.keySecret);
    if (!validation.valid) {
      console.warn('⚠️ Razorpay Configuration Warning:', validation.error);
    }
    return validation.valid;
  }

  // Convert rupees to paise (Razorpay uses paise)
  convertToPaise(amountInRupees: number): number {
    return Math.round(amountInRupees * 100);
  }

  // Convert paise to rupees
  convertToRupees(amountInPaise: number): number {
    return amountInPaise / 100;
  }

  // Format amount for display
  formatAmount(amount: number): string {
    return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  // Create Razorpay order (for production, this should be done via backend API)
  // For development, we'll use direct payment without order_id
  async createOrder(orderData: {
    amount: number;
    currency?: string;
    receipt?: string;
    notes?: Record<string, any>;
    patientId?: string;
    transactionType?: string;
  }): Promise<{ success: boolean; order?: any; orderId?: string; error?: string }> {
    try {
      // Validate amount (minimum 1 rupee = 100 paise)
      if (orderData.amount < 100) {
        throw new Error('Minimum amount should be ₹1');
      }

      console.log('📞 Calling Firebase Function to create Razorpay order...');

      // Call Firebase Function to create REAL Razorpay order with auto-capture
      const createRazorpayOrder = httpsCallable(functions, 'createRazorpayOrder');
      
      const result = await createRazorpayOrder({
        amount: Math.round(orderData.amount),
        currency: orderData.currency || 'INR',
        receipt: orderData.receipt || generateReceiptId('admin', orderData.patientId),
        patientId: orderData.patientId,
        transactionType: orderData.transactionType || 'consultation',
        createdVia: 'admin_dashboard',
        notes: {
          patient_id: orderData.patientId,
          transaction_type: orderData.transactionType || 'consultation',
          hospital_name: this.config.hospitalInfo.name,
          created_via: 'admin_dashboard',
          ...orderData.notes
        }
      });

      const data: any = result.data;

      if (!data.success) {
        throw new Error(data.message || 'Failed to create order');
      }

      const order = data.order;
      
      console.log('✅ Real Razorpay order created successfully:', order.id);
      console.log('🎯 Auto-capture enabled: payment_capture =', order.payment_capture);

      return {
        success: true,
        order: order,
        orderId: order.id
      };
    } catch (error: any) {
      console.error('❌ Error calling Firebase Function:', error);
      console.log('🔄 Attempting direct Razorpay API call as fallback...');
      
      // Fallback: Call Razorpay API directly
      try {
        const response = await fetch('https://api.razorpay.com/v1/orders', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Basic ' + btoa(this.config.keyId + ':' + this.config.keySecret)
          },
          body: JSON.stringify({
            amount: Math.round(orderData.amount),
            currency: orderData.currency || 'INR',
            receipt: orderData.receipt || generateReceiptId('admin', orderData.patientId),
            payment_capture: 1, // Enable auto-capture
            notes: {
              patient_id: orderData.patientId,
              transaction_type: orderData.transactionType || 'consultation',
              hospital_name: this.config.hospitalInfo.name,
              created_via: 'admin_dashboard_direct',
              ...orderData.notes
            }
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(`Razorpay API Error: ${errorData.error?.description || 'Unknown error'}`);
        }

        const order = await response.json();
        
        console.log('✅ Razorpay order created via direct API:', order.id);
        console.log('🎯 Auto-capture enabled: payment_capture =', order.payment_capture);
        
        return {
          success: true,
          order: order,
          orderId: order.id
        };
        
      } catch (apiError: any) {
        console.error('❌ Direct API call also failed:', apiError);
        return {
          success: false,
          error: apiError.message || 'Failed to create order'
        };
      }
    }
  }

  // Initialize Razorpay checkout
  async initiatePayment(
    paymentData: PaymentCreationData,
    onSuccess: (response: RazorpayResponse) => void,
    onFailure: (error: RazorpayError) => void
  ): Promise<void> {
    try {
      console.log('🚀 Initiating Razorpay payment...', paymentData);
      
      // Wait for Razorpay SDK to be available
      const razorpayAvailable = await this.waitForRazorpay();
      
      if (!razorpayAvailable || typeof window.Razorpay === 'undefined') {
        console.error('❌ Razorpay SDK not available after waiting');
        throw new Error('Razorpay SDK failed to load. Please check your internet connection and refresh the page.');
      }

      console.log('✅ Razorpay SDK is ready');

      // Convert amount to paise
      const amountInPaise = this.convertToPaise(paymentData.amount);
      console.log('💰 Amount in paise:', amountInPaise, 'from rupees:', paymentData.amount);

      // Generate receipt ID
      const receipt = generateReceiptId(paymentData.paymentType, paymentData.patientId);
      console.log('🧾 Receipt ID:', receipt);

      // For development: Use direct payment without order_id
      // In production: Uncomment order creation and use real order_id
      
      console.log('🔧 Development mode: Using direct payment without order_id');
      
      // Prepare Razorpay checkout options (development mode)
      const options: RazorpayOptions = {
        key: this.config.keyId,
        amount: amountInPaise,
        currency: 'INR',
        name: this.config.hospitalInfo.name,
        description: paymentData.description,
        ...(this.config.hospitalInfo.logo && { image: this.config.hospitalInfo.logo }),
        // order_id: order.id, // Commented out for development
        handler: (response: RazorpayResponse) => {
          console.log('✅ Payment successful:', response);
          
          // For direct payment, we only get payment_id, no order_id
          const mockResponse = {
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_order_id: response.razorpay_order_id || `dev_${Date.now()}`,
            razorpay_signature: response.razorpay_signature || 'dev_signature'
          };
          
          onSuccess(mockResponse);
        },
        prefill: {
          name: paymentData.patientName,
          email: paymentData.patientEmail || '',
          contact: paymentData.patientPhone || ''
        },
        notes: {
          patient_id: paymentData.patientId,
          patient_name: paymentData.patientName,
          payment_type: paymentData.paymentType,
          description: paymentData.description
        },
        theme: {
          color: this.config.checkoutOptions.theme.color
        },
        modal: {
          ondismiss: () => {
            console.log('⚠️ Payment cancelled by user');
            onFailure({
              code: 'PAYMENT_CANCELLED',
              description: 'Payment was cancelled by user',
              source: 'customer',
              step: 'payment_cancelled',
              reason: 'user_cancelled',
              metadata: {
                order_id: 'dev_cancelled'
              } as any
            });
          },
          backdropclose: false,
          escape: true,
          handleback: true,
          confirm_close: true
        }
      };

      console.log('🎯 Razorpay options prepared:', {
        key: options.key,
        amount: options.amount,
        currency: options.currency,
        order_id: options.order_id,
        name: options.name
      });

      // Initialize Razorpay checkout
      console.log('🚀 Creating Razorpay instance...');
      console.log('🔍 Razorpay constructor available:', typeof window.Razorpay);
      console.log('🔍 Options being passed:', JSON.stringify({
        key: options.key,
        amount: options.amount,
        currency: options.currency,
        order_id: options.order_id,
        name: options.name,
        description: options.description
      }, null, 2));
      
      const rzp = new window.Razorpay(options);
      console.log('✅ Razorpay instance created successfully:', rzp);
      console.log('✅ Razorpay instance methods:', Object.getOwnPropertyNames(rzp));

      // Enhanced error handling for payment failures
      rzp.on('payment.failed', (response: any) => {
        console.error('❌ Payment failed event:', response);
        const error = response.error || {};
        
        // Handle specific API errors (400 Bad Request, etc.)
        let errorMessage = 'Payment failed';
        if (error.code === 'BAD_REQUEST_ERROR') {
          errorMessage = 'Invalid payment request. Please check your configuration.';
        } else if (error.description) {
          errorMessage = error.description;
        }
        
        onFailure({
          code: error.code || 'PAYMENT_FAILED',
          description: errorMessage,
          source: error.source || 'gateway',
          step: error.step || 'payment_failed',
          reason: error.reason || 'unknown',
          metadata: {
            order_id: 'dev_payment',
            payment_id: response.payment_id || null,
            error_details: error,
            api_error: true
          } as any
        });
      });

      // Open Razorpay checkout with improved error handling
      console.log('🎨 Opening Razorpay checkout...');
      setTimeout(() => {
        try {
          rzp.open();
          console.log('✅ Razorpay checkout opened successfully');
        } catch (openError: any) {
          console.error('❌ Error opening Razorpay checkout:', openError);
          onFailure({
            code: 'CHECKOUT_OPEN_ERROR',
            description: `Failed to open Razorpay checkout: ${openError.message}`,
            source: 'internal',
            step: 'checkout_open',
            reason: 'open_failed',
            metadata: {
              order_id: 'dev_open_error',
              error: openError.message,
              development_mode: true
            } as any
          });
        }
      }, 150); // Slightly longer delay to ensure everything is ready

    } catch (error: any) {
      console.error('❌ Error initiating payment:', error);
      console.error('Error stack:', error.stack);
      
      // Enhanced error categorization
      let errorCode = 'INIT_ERROR';
      let errorStep = 'initialization';
      
      if (error.message.includes('SDK')) {
        errorCode = 'SDK_ERROR';
        errorStep = 'sdk_loading';
      } else if (error.message.includes('order')) {
        errorCode = 'ORDER_ERROR';
        errorStep = 'order_creation';
      } else if (error.message.includes('network') || error.message.includes('fetch')) {
        errorCode = 'NETWORK_ERROR';
        errorStep = 'network';
      }
      
      onFailure({
        code: errorCode,
        description: error.message || 'Failed to initiate payment',
        source: 'internal',
        step: errorStep,
        reason: 'initialization_failed',
        metadata: {
          error_type: error.name,
          error_message: error.message,
          stack: error.stack
        } as any
      });
    }
  }

  // Store payment record in Firebase
  async storePaymentRecord(paymentRecord: PaymentRecord): Promise<{ success: boolean; paymentId?: string; error?: string }> {
    try {
      console.log('💾 Storing payment record in Firebase...', paymentRecord);

      const docRef = await addDoc(collection(db, 'payments'), {
        ...paymentRecord,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });

      console.log('✅ Payment record stored with ID:', docRef.id);

      return {
        success: true,
        paymentId: docRef.id
      };
    } catch (error: any) {
      console.error('❌ Error storing payment record:', error);
      return {
        success: false,
        error: error.message || 'Failed to store payment record'
      };
    }
  }

  // Update payment record in Firebase
  async updatePaymentRecord(
    paymentId: string, 
    updates: Partial<PaymentRecord>
  ): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('🔄 Updating payment record:', paymentId, updates);

      await updateDoc(doc(db, 'payments', paymentId), {
        ...updates,
        updatedAt: Timestamp.now()
      });

      console.log('✅ Payment record updated successfully');

      return { success: true };
    } catch (error: any) {
      console.error('❌ Error updating payment record:', error);
      return {
        success: false,
        error: error.message || 'Failed to update payment record'
      };
    }
  }

  // Verify payment signature (enhanced client-side check)
  verifyPaymentSignature(
    orderId: string,
    paymentId: string,
    signature: string
  ): boolean {
    try {
      // Basic validation - ensure all required fields are present
      if (!orderId || !paymentId || !signature) {
        console.error('❌ Missing required signature verification fields');
        return false;
      }
      
      // Validate format
      if (!orderId.startsWith('order_') || !paymentId.startsWith('pay_')) {
        console.error('❌ Invalid order or payment ID format');
        return false;
      }
      
      // Note: In production, signature verification should be done on backend
      // This is just a basic client-side check for format validation
      console.log('📝 Signature verification data (format validated):', {
        orderId,
        paymentId,
        signature: signature.substring(0, 10) + '...'
      });
      
      // For now, return true after basic validation
      // In production, send to backend for actual HMAC verification
      return true;
    } catch (error: any) {
      console.error('❌ Error verifying payment signature:', error);
      return false;
    }
  }

  // Get payment status color
  getPaymentStatusColor(status: string): string {
    switch (status.toLowerCase()) {
      case 'paid':
      case 'captured':
        return '#10B981'; // Green
      case 'pending':
      case 'authorized':
        return '#F59E0B'; // Amber
      case 'failed':
      case 'refunded':
        return '#EF4444'; // Red
      default:
        return '#6B7280'; // Gray
    }
  }

  // Get transaction type from payment type
  getTransactionType(paymentType: string): string {
    const typeMap: Record<string, string> = {
      'consultation fee': RAZORPAY_CONFIG.transactionTypes.CONSULTATION,
      'registration fee': RAZORPAY_CONFIG.transactionTypes.REGISTRATION,
      'treatment fee': RAZORPAY_CONFIG.transactionTypes.TREATMENT,
      'room charges': RAZORPAY_CONFIG.transactionTypes.ROOM_CHARGES,
      'medicine charges': RAZORPAY_CONFIG.transactionTypes.MEDICINE,
      'test charges': RAZORPAY_CONFIG.transactionTypes.TESTS,
      'surgery fee': RAZORPAY_CONFIG.transactionTypes.SURGERY,
      'emergency fee': RAZORPAY_CONFIG.transactionTypes.EMERGENCY,
      'other': RAZORPAY_CONFIG.transactionTypes.OTHER
    };

    return typeMap[paymentType.toLowerCase()] || RAZORPAY_CONFIG.transactionTypes.OTHER;
  }

  // Debug helper to test Razorpay availability
  testRazorpayAvailability(): { available: boolean; error?: string; details: any } {
    const details = {
      razorpayGlobal: typeof window.Razorpay,
      windowObject: typeof window,
      config: {
        keyId: this.config.keyId,
        isValidKey: this.config.keyId.startsWith('rzp_'),
        environment: this.config.environment || 'production'
      },
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString()
    };

    if (typeof window.Razorpay === 'undefined') {
      return {
        available: false,
        error: 'Razorpay SDK not loaded. Check if script tag is present and network is working.',
        details
      };
    }

    try {
      // Try to create a test instance (won't open, just test creation)
      const testOptions = {
        key: this.config.keyId,
        amount: 100,
        currency: 'INR',
        name: 'Test',
        description: 'Test',
        handler: () => {} // Required but won't be called
      };
      
      new window.Razorpay(testOptions);
      
      return {
        available: true,
        details
      };
    } catch (error: any) {
      return {
        available: false,
        error: `Razorpay SDK error: ${error.message}`,
        details: { ...details, testError: error.message }
      };
    }
  }
}

// Export singleton instance
const razorpayService = new RazorpayService();
export default razorpayService;
